import { useEffect } from 'react';

import { useAuth } from './useAuth.jsx';

const META_PIXEL_SCRIPT_ID = 'meta-pixel-script';
const META_PIXEL_NOSCRIPT_PREFIX = 'meta-pixel-noscript';

const metaPixelConfig = {
  login: {
    eventName: 'login_page',
    pageType: 'login',
    contentName: 'User Login',
  },
  register: {
    eventName: 'registration_page',
    pageType: 'registration',
    contentName: 'User Registration',
  },
  'alternative-therapy-form': {
    eventName: 'alternative_therapy_form',
    pageType: 'visit_alternative_therapy_page',
    contentName: 'alternative_therapy_form visit',
  },
} as const;

type MetaPixelPage = keyof typeof metaPixelConfig;

const ensureMetaPixelBaseCode = (pixelId: string) => {
  if (window.__metaPixelInitialized) {
    return;
  }

  if (!window.fbq) {
    const fbq = ((...args: unknown[]) => {
      if (fbq.callMethod) {
        fbq.callMethod(...args);
        return;
      }

      fbq.queue.push(args);
    }) as NonNullable<Window['fbq']>;

    fbq.push = (...args: unknown[]) => {
      fbq(...args);
    };
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];

    window.fbq = fbq;

    if (!window._fbq) {
      window._fbq = fbq;
    }
  }

  if (!document.getElementById(META_PIXEL_SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = META_PIXEL_SCRIPT_ID;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    script.async = true;
    script.onerror = () => {
      console.warn('[Meta Pixel] Failed to load fbevents.js — tracking will be unavailable');
    };
    document.head.appendChild(script);
  }

  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');
  window.__metaPixelInitialized = true;
};

const ensureMetaPixelNoscriptFallback = ({ pixelId, eventName }: { pixelId: string; eventName: string }) => {
  const noscriptId = `${META_PIXEL_NOSCRIPT_PREFIX}-${eventName}`;

  if (document.getElementById(noscriptId)) {
    return;
  }

  const noscript = document.createElement('noscript');
  noscript.id = noscriptId;

  const img = document.createElement('img');
  img.height = 1;
  img.width = 1;
  img.style.display = 'none';
  img.src = `https://www.facebook.com/tr?id=${encodeURIComponent(pixelId)}&ev=${encodeURIComponent(eventName)}&noscript=1`;

  noscript.appendChild(img);

  if (document.body) {
    document.body.appendChild(noscript);
    return;
  }

  document.head.appendChild(noscript);
};

export const useMetaPixel = (page: MetaPixelPage) => {
  const { user } = useAuth();

  useEffect(() => {
    // If user is authenticated, we skip loading Meta Pixel.
    if (Boolean(user)) {
      return;
    }

    const pixelId = import.meta.env.VITE_APP_META_PIXEL_ID;

    if (!pixelId) {
      return;
    }

    const config = metaPixelConfig[page];

    if (!config) {
      return;
    }

    ensureMetaPixelBaseCode(pixelId);

    if (window.fbq) {
      window.fbq('trackCustom', config.eventName, {
        page_type: config.pageType,
        content_name: config.contentName,
      });
    }

    ensureMetaPixelNoscriptFallback({ pixelId, eventName: config.eventName });
  }, [page, user]);
};
